import { logout, useGetUserAdminProfile } from '@sunrise-ui/api-client';
import {
  Header,
  HeaderTitle,
  Loader,
  LogoIcon,
  Modal,
  useAuthStore,
  useAuthTimeout,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';

type WithAuthGateOptions = {
  /** Whether to require authentication. Defaults to true. */
  requireAuth?: boolean;
};

/**
 * If this is ever disabled on native we need dedicated focus handling for the modal
 * @param Component
 * @param options
 */
export function withAuthGate<TComponentProps extends object>(
  Component: React.ComponentType<TComponentProps>,
  options: WithAuthGateOptions = {},
) {
  const { requireAuth = true } = options;
  const WithAuthGate = (props: TComponentProps) => {
    const {
      isAuthenticated,
      isOnboarded,
      openAuthFlowModal,
      hasCheckedAuth,
      hasCheckedOnboarding,
      modalFlow,
      forceLogin,
      setForceLogin,
    } = useAuthStore();

    const email = forceLogin?.email;
    const isForceLoginActive = !!forceLogin;

    const { data: userAdminProfile, status } = useGetUserAdminProfile();
    const userEmail = userAdminProfile?.email;
    const styles = useDynamicStyles(createStyles);

    const [showLoader, setShowLoader] = useState(isForceLoginActive);

    // Give AuthFlowModal time to actually render and become visible
    const hideLoaderWithDelay = useCallback(() => {
      setTimeout(() => {
        setShowLoader(false);
      }, 1000);
    }, []);

    // Safety timeout: Force auth check completion if it takes too long
    useAuthTimeout({ enabled: requireAuth || isForceLoginActive });

    const clearForceLoginState = useCallback(() => {
      setForceLogin(undefined);
    }, [setForceLogin]);

    // Handle authentication gating (both force login and normal)
    useEffect(() => {
      // Skip if auth not required and no force login
      if (!requireAuth && !isForceLoginActive) return;
      if (!hasCheckedAuth) return;

      // User not authenticated - show auth modal
      if (!isAuthenticated) {
        if (modalFlow.isVisible) return; // Modal already open
        openAuthFlowModal(
          { onFlowComplete: clearForceLoginState },
          {
            currentStep: email ? 'confirm' : 'login',
            email,
          },
        );
        hideLoaderWithDelay();
        return;
      }

      // User is authenticated - check email mismatch (force login only)
      if (isForceLoginActive && email) {
        if (status === 'pending') {
          return; // Wait for profile to load
        }

        if (!userEmail || userEmail.toLowerCase() !== email.toLowerCase()) {
          // Email mismatch - logout and show auth modal
          if (modalFlow.isVisible) return; // Modal already open
          void logout();
          openAuthFlowModal(
            { onFlowComplete: clearForceLoginState },
            {
              currentStep: 'confirm',
              email,
            },
          );
          hideLoaderWithDelay();
          return;
        }
      }

      // Check onboarding
      if (!hasCheckedOnboarding) return;

      if (!isOnboarded) {
        if (modalFlow.isVisible) return; // Modal already open
        openAuthFlowModal({ onFlowComplete: clearForceLoginState });
        hideLoaderWithDelay();
        return;
      }

      // All checks passed - clear force login state if applicable
      clearForceLoginState();
      setShowLoader(false);
    }, [
      requireAuth,
      isForceLoginActive,
      hasCheckedAuth,
      isAuthenticated,
      email,
      status,
      userEmail,
      hasCheckedOnboarding,
      isOnboarded,
      modalFlow.isVisible,
      openAuthFlowModal,
      clearForceLoginState,
      hideLoaderWithDelay,
    ]);

    return (
      <>
        {showLoader && (
          <Modal
            visible={true}
            hide={() => {}}
            header={
              <Header
                headerTitle={
                  <View style={styles.headerLogoContainer}>
                    <HeaderTitle logo={LogoIcon} />
                  </View>
                }
                showShadow
              />
            }
          >
            <Loader />
          </Modal>
        )}
        <Component {...props} />
      </>
    );
  };

  WithAuthGate.displayName = `withAuthGate(${Component.displayName || Component.name})`;

  return WithAuthGate;
}

const createStyles = () =>
  StyleSheet.create({
    headerLogoContainer: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 40,
      minWidth: 100,
    },
  });
